/**
 * Pure predicate evaluator for rule conditions. Resolves `ctx:*` ids from the
 * `RuleContext` and field ids from the flat value map, then applies the
 * operator. Recursive over `all` / `any` / `not` groups.
 */
import type { Condition, ConditionGroup, QualifiedId, RuleCondition } from '../../config/fieldRulesConfig';
import type { RuleContext, RuleValues } from '../types';

const CTX_PREFIX = 'ctx:';

/** Reads a qualified id's value from ctx (`ctx:*`) or the value map. */
export function resolveSource(source: QualifiedId, values: RuleValues, ctx: RuleContext): unknown {
    if (source.startsWith(CTX_PREFIX)) {
        return ctx[source.slice(CTX_PREFIX.length)];
    }
    return values[source];
}

const isEmpty = (value: unknown): boolean =>
    value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

const toNumber = (value: unknown): number => Number(value);

const isCondition = (cond: RuleCondition): cond is Condition => 'source' in cond;

function evaluateLeaf(cond: Condition, values: RuleValues, ctx: RuleContext): boolean {
    const actual = resolveSource(cond.source, values, ctx);
    const expected = cond.value;
    switch (cond.op) {
        case 'eq':
            return actual === expected;
        case 'neq':
            return actual !== expected;
        case 'in':
            return Array.isArray(expected) && expected.includes(actual as never);
        case 'notIn':
            return !(Array.isArray(expected) && expected.includes(actual as never));
        case 'isEmpty':
            return isEmpty(actual);
        case 'isNotEmpty':
            return !isEmpty(actual);
        case 'truthy':
            return !!actual;
        case 'falsy':
            return !actual;
        case 'gt':
            return toNumber(actual) > toNumber(expected);
        case 'gte':
            return toNumber(actual) >= toNumber(expected);
        case 'lt':
            return toNumber(actual) < toNumber(expected);
        case 'lte':
            return toNumber(actual) <= toNumber(expected);
        default:
            return false;
    }
}

export function evaluateCondition(
    cond: RuleCondition | undefined,
    values: RuleValues,
    ctx: RuleContext,
): boolean {
    if (!cond) return true;
    if (isCondition(cond)) return evaluateLeaf(cond, values, ctx);

    const group = cond as ConditionGroup;
    if (group.all && !group.all.every((c) => evaluateCondition(c, values, ctx))) return false;
    if (group.any && !group.any.some((c) => evaluateCondition(c, values, ctx))) return false;
    if (group.not && evaluateCondition(group.not, values, ctx)) return false;
    return true;
}

/** Flattens every field/context id referenced by a condition (for trigger derivation). */
export function conditionSources(cond: RuleCondition | undefined): QualifiedId[] {
    if (!cond) return [];
    if (isCondition(cond)) return [cond.source];

    const group = cond as ConditionGroup;
    const out: QualifiedId[] = [];
    group.all?.forEach((c) => out.push(...conditionSources(c)));
    group.any?.forEach((c) => out.push(...conditionSources(c)));
    if (group.not) out.push(...conditionSources(group.not));
    return out;
}
